import React from 'react';
import EndpointCard from '../EndpointCard';
import CodeBlock from '../CodeBlock';

const SOPProposals = () => {
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  return (
    <section className="section">
      <h2 className="section-title">Change Proposals</h2>
      <p className="text-gray-300 mb-6 leading-7 max-w-[65ch]">
        Review and manage change proposals for SOPs. Proposals are automatically generated when you answer
        clarification questions. Review the proposed changes, approve them to apply automatically, or reject
        them if they don't meet your requirements.
      </p>

      <EndpointCard
        method="GET"
        path="/sops/{sop_uuid}/proposals"
        title="List Change Proposals"
        description="Retrieves all change proposals for an SOP. Can optionally filter by status (PENDING, APPROVED, REJECTED, APPLIED)."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            sop_uuid: { required: true, description: 'The unique identifier of the SOP' }
          },
          query: {
            status: { required: false, description: 'Filter by proposal status (PENDING, APPROVED, REJECTED, APPLIED)' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Query Parameters:</h4>
            <table className="parameter-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>status</code></td>
                  <td>string</td>
                  <td><span className="optional-badge">Optional</span></td>
                  <td>Filter by proposal status (PENDING, APPROVED, REJECTED, APPLIED)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "${BASE_URL}/sops/{sop_uuid}/proposals?status=PENDING" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`[
  {
    "uuid": "{proposal_uuid}",
    "clarification_uuid": "{clarification_uuid}",
    "sop_uuid": "{sop_uuid}",
    "step_uuid": "{step_uuid}",
    "status": "PENDING",
    "before_snapshot": {
      "scope": {...},
      "title": "...",
      "steps": {...}
    },
    "proposed_changes": {
      "step_updates": [
        {
          "step_uuid": "{step_uuid}",
          "updates": {...},
          "diffs": {
            "action_text": {
              "before": "...",
              "after": "..."
            }
          }
        }
      ]
    },
    "justification": "Based on the clarification answer, we should update this step...",
    "analysis": {
      "impact": "Enhances the SOP by providing a clear next step...",
      "confidence": "HIGH"
    },
    "created_by_user_uuid": "{user_uuid}",
    "reviewed_by_user_uuid": null,
    "created_at": "2024-01-15T10:05:30Z",
    "updated_at": "2024-01-15T10:05:30Z"
  }
]`} />
          </div>

          <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
            <p className="text-sm text-blue-200">
              <strong>Reviewing proposals:</strong> The <code className="bg-blue-900/50 px-1 py-0.5 rounded">proposed_changes</code>
              field contains structured diffs showing <code className="bg-blue-900/50 px-1 py-0.5 rounded">before</code> and
              <code className="bg-blue-900/50 px-1 py-0.5 rounded">after</code> states for modified fields.
            </p>
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="GET"
        path="/clarifications/{clarification_uuid}/proposal"
        title="Get Proposal for Clarification"
        description="Retrieves the change proposal associated with a specific clarification. Use this to get the proposal generated from a particular clarification answer."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            clarification_uuid: { required: true, description: 'The unique identifier of the clarification' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "${BASE_URL}/clarifications/{clarification_uuid}/proposal" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`{
  "uuid": "{proposal_uuid}",
  "clarification_uuid": "{clarification_uuid}",
  "sop_uuid": "{sop_uuid}",
  "step_uuid": "{step_uuid}",
  "status": "PENDING",
  "before_snapshot": {...},
  "proposed_changes": {...},
  "justification": "...",
  "analysis": {...}
}`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="POST"
        path="/proposals/{proposal_uuid}/review"
        title="Review Proposal"
        description="Reviews a change proposal by approving or rejecting it. Approved proposals trigger automatic application of changes to the SOP in the background. Rejected proposals mark the clarification as rejected."
        tags={['Authentication: Required', 'Async: Yes']}
        parameters={{
          path: {
            proposal_uuid: { required: true, description: 'The unique identifier of the proposal to review' }
          }
        }}
        requestBody={{
          action: "approve",
          review_notes: "These changes look good and align with our policy."
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Request Body:</h4>
            <CodeBlock code={`{
  "action": "approve",
  "review_notes": "These changes look good and align with our policy."
}`} />
            <p className="text-xs text-gray-400 mt-2">
              <strong>action</strong> (required): Either <code className="bg-gray-800 px-1 py-0.5 rounded">approve</code> or <code className="bg-gray-800 px-1 py-0.5 rounded">reject</code><br/>
              <strong>review_notes</strong> (optional): Notes about the review decision
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example (Approve):</h4>
            <CodeBlock code={`curl -X POST "${BASE_URL}/proposals/{proposal_uuid}/review" \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "action": "approve",
    "review_notes": "These changes look good."
  }'`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example (Reject):</h4>
            <CodeBlock code={`curl -X POST "${BASE_URL}/proposals/{proposal_uuid}/review" \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "action": "reject",
    "review_notes": "This doesn't match our current policy."
  }'`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK - Approved):</h4>
            <CodeBlock code={`{
  "uuid": "{proposal_uuid}",
  "clarification_uuid": "{clarification_uuid}",
  "sop_uuid": "{sop_uuid}",
  "status": "APPROVED",
  "reviewed_by_user_uuid": "{reviewer_uuid}",
  "before_snapshot": {...},
  "proposed_changes": {...},
  "justification": "...",
  "created_at": "2024-01-15T10:05:30Z",
  "updated_at": "2024-01-15T10:10:00Z"
}`} />
          </div>

          <div className="p-4 bg-green-900/20 border border-green-700/50 rounded-lg">
            <p className="text-sm text-green-200">
              <strong>Approval process:</strong> Approved proposals trigger a background task to apply changes
              directly to the SOP. Rejected proposals mark the clarification as rejected.
            </p>
          </div>
        </div>
      </EndpointCard>
    </section>
  );
};

export default SOPProposals;
